import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconAlertCircle,
  IconDownload,
  IconEye,
  IconFileText,
  IconInfoCircle,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import type { Conclusion } from '../lib/types';

/**
 * Onglet « Nos conclusions » du dossier (Sprint 3 T1).
 *
 * Drop-zone manuelle EXCLUSIVE des conclusions effectivement déposées
 * (Word/PDF). Le générateur IA (onglet « Génération (drafts IA) » sous
 * Actes) ne dépose JAMAIS automatiquement ici — l'avocat télécharge le
 * draft, retravaille dans Word, puis re-dépose ici la version finale.
 *
 * Métadonnées capturées à l'upload : date de dépôt, audience visée,
 * origine (concluant M24 / MP / PC / autre), statut. Optionnel : lien
 * de traçabilité vers le draft IA dont l'acte est issu.
 */

type Origine = 'concluant' | 'mp' | 'partie_civile' | 'autre_partie' | 'autre';
type Statut = 'draft' | 'a_deposer' | 'deposee' | 'recue' | 'archivee';

type ConclusionFile = {
  id: string;
  dossier_id: string;
  title: string;
  filename_original: string;
  origine: Origine;
  statut: Statut;
  date_depot: string | null;
  juridiction: string | null;
  reference_depot: string | null;
  audience_visee: string | null;
  generated_from_id: string | null;
  version_label: string;
  file_size_bytes: number;
  content_type: string | null;
  commentaire: string | null;
  uploaded_at: string;
};

const ORIGINE_LABELS: Record<Origine, string> = {
  concluant: 'Concluant (M24)',
  mp: 'Ministère public',
  partie_civile: 'Partie civile',
  autre_partie: 'Autre partie',
  autre: 'Autre',
};

const STATUT_LABELS: Record<Statut, string> = {
  draft: 'Brouillon',
  a_deposer: 'À déposer',
  deposee: 'Déposée',
  recue: 'Reçue',
  archivee: 'Archivée',
};

const STATUT_BADGE: Record<Statut, string> = {
  draft: 'bg-cat-imp-pastel text-cat-imp-text',
  a_deposer: 'bg-accent-light text-accent-text',
  deposee: 'bg-cat-fav-pastel text-cat-fav',
  recue: 'bg-paper-subtle text-text-secondary',
  archivee: 'bg-paper-subtle text-text-tertiary',
};

const ACCEPTED_MIMES = '.docx,.doc,.pdf,.odt,.rtf';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

type Props = {
  dossierId: string;
};

type PendingUpload = {
  file: File;
  title: string;
  origine: Origine;
  statut: Statut;
  date_depot: string;
  audience_visee: string;
  generated_from_id: string;
  juridiction: string;
  reference_depot: string;
  commentaire: string;
};

export default function ConclusionsStorageTab({ dossierId }: Props) {
  const [items, setItems] = useState<ConclusionFile[]>([]);
  const [drafts, setDrafts] = useState<Conclusion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState<PendingUpload | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const [files, gens] = await Promise.all([
        api.get<ConclusionFile[]>(`/dossiers/${dossierId}/conclusion-files`),
        api.get<Conclusion[]>(`/dossiers/${dossierId}/conclusions`).catch(() => [] as Conclusion[]),
      ]);
      setItems(files);
      setDrafts(gens);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    const out: Record<Origine, ConclusionFile[]> = {
      concluant: [],
      mp: [],
      partie_civile: [],
      autre_partie: [],
      autre: [],
    };
    for (const f of items) out[f.origine].push(f);
    return out;
  }, [items]);

  const finalizedDrafts = useMemo(
    () => drafts.filter((d) => d.status === 'finalized' || d.status === 'draft'),
    [drafts]
  );

  const onFilesSelected = (files: FileList | File[]) => {
    setDragOver(false);
    const list = Array.from(files);
    if (list.length === 0) return;
    if (list.length > 1) {
      toast.error(
        'Dépose un seul fichier à la fois — chaque conclusion déposée porte ses propres métadonnées (audience, date).'
      );
      return;
    }
    const f = list[0];
    if (!f) return;
    setPending({
      file: f,
      title: f.name.replace(/\.[^.]+$/, ''),
      origine: 'concluant',
      statut: 'deposee',
      date_depot: new Date().toISOString().slice(0, 10),
      audience_visee: '',
      generated_from_id: '',
      juridiction: '',
      reference_depot: '',
      commentaire: '',
    });
  };

  const onConfirmUpload = async () => {
    if (!pending) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', pending.file);
    fd.append(
      'metadata',
      JSON.stringify({
        title: pending.title.trim() || pending.file.name,
        origine: pending.origine,
        statut: pending.statut,
        date_depot: pending.date_depot || null,
        audience_visee: pending.audience_visee.trim() || null,
        generated_from_id: pending.generated_from_id || null,
        juridiction: pending.juridiction.trim() || null,
        reference_depot: pending.reference_depot.trim() || null,
        commentaire: pending.commentaire.trim() || null,
      })
    );
    try {
      const created = await api.postForm<ConclusionFile>(
        `/dossiers/${dossierId}/conclusion-files`,
        fd
      );
      setItems((prev) => [created, ...prev]);
      toast.success(`« ${created.title} » déposé (${created.version_label})`);
      setPending(null);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload échoué');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (item: ConclusionFile) => {
    if (!confirm(`Supprimer « ${item.title} » (${item.version_label}) ?`)) return;
    try {
      await api.delete(`/conclusion-files/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Suppression échouée');
    }
  };

  const onDownload = (item: ConclusionFile) => {
    void api
      .downloadBlob(`/conclusion-files/${item.id}/download`, item.filename_original)
      .catch((e) => toast.error(e instanceof ApiError ? e.message : 'Téléchargement échoué'));
  };

  const onPatchOrigine = async (item: ConclusionFile, origine: Origine) => {
    try {
      const updated = await api.patch<ConclusionFile>(`/conclusion-files/${item.id}`, { origine });
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Mise à jour échouée');
    }
  };

  const onPatchStatut = async (item: ConclusionFile, statut: Statut) => {
    try {
      const updated = await api.patch<ConclusionFile>(`/conclusion-files/${item.id}`, { statut });
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Mise à jour échouée');
    }
  };

  if (loading) {
    return <div className="px-6 py-5 text-[13px] text-text-secondary">Chargement…</div>;
  }

  return (
    <div className="space-y-5 px-6 py-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">Nos conclusions</h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Conclusions effectivement déposées (.docx/.pdf/.doc) — ce que la procédure a vu.
          </p>
        </div>
        <Btn type="button" variant="primary" onClick={() => fileInputRef.current?.click()}>
          <IconUpload size={16} stroke={1.6} />
          <span>Déposer une conclusion</span>
        </Btn>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_MIMES}
          className="hidden"
          onChange={(e) => {
            if (e.target.files) onFilesSelected(e.target.files);
            e.target.value = '';
          }}
        />
      </header>

      <div className="flex items-start gap-2 rounded-md border border-cat-imp/40 bg-cat-imp-pastel p-3 text-[12.5px] text-cat-imp-text">
        <IconAlertCircle size={16} stroke={1.7} className="mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">Drop-zone manuelle uniquement</p>
          <p className="mt-0.5 leading-relaxed">
            Les drafts IA générés depuis <strong>Actes → Génération (drafts IA)</strong> ne sont
            <strong> jamais copiés ici automatiquement</strong>. Workflow : (1) génère un draft, (2)
            télécharge le .docx, (3) retravaille dans Word, (4) re-dépose ici la version définitive
            — celle que tu envoies effectivement à la partie adverse / au tribunal.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-cat-def/40 bg-cat-def-pastel p-3 text-[12.5px] text-cat-def">
          {error}
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          onFilesSelected(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed p-6 text-[13px] transition ${
          dragOver
            ? 'border-accent bg-accent-light text-accent-text'
            : 'border-rule-strong bg-paper-elevated text-text-secondary'
        }`}
      >
        <IconUpload size={28} stroke={1.6} />
        <p className="font-medium text-text-primary">
          Dépose ici tes conclusions définitives (.docx, .doc, .pdf)
        </p>
        <p className="text-[11.5px] text-text-tertiary">
          Versionnage automatique si même nom + origine. Tu pourras renseigner audience + date de
          dépôt à l'étape suivante.
        </p>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-rule bg-paper-elevated p-6 text-center text-[13px] text-text-tertiary">
          Aucune conclusion déposée pour ce dossier.
        </div>
      ) : (
        <div className="space-y-4">
          {(Object.keys(ORIGINE_LABELS) as Origine[]).map((origine) => {
            const list = grouped[origine];
            if (list.length === 0) return null;
            return (
              <section key={origine} className="space-y-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.5px] text-text-tertiary">
                  {ORIGINE_LABELS[origine]} · {list.length}
                </h2>
                <ul className="space-y-1.5">
                  {list.map((it) => (
                    <li
                      key={it.id}
                      className="group flex items-start gap-3 rounded-md border border-rule bg-paper-elevated p-3 text-[13px]"
                    >
                      <IconFileText
                        size={18}
                        stroke={1.6}
                        className="mt-0.5 shrink-0 text-text-secondary"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-medium text-text-primary">{it.title}</span>
                          <span className="rounded-pill bg-paper-subtle px-1.5 py-[1px] font-mono text-[10px] text-text-tertiary">
                            {it.version_label}
                          </span>
                          <span
                            className={`rounded-pill px-2 py-[1px] font-mono text-[10px] ${STATUT_BADGE[it.statut]}`}
                          >
                            {STATUT_LABELS[it.statut]}
                          </span>
                          {it.audience_visee && (
                            <span className="rounded-pill bg-accent-light px-1.5 py-[1px] text-[10.5px] text-accent-text">
                              {it.audience_visee}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-text-tertiary">
                          <span className="truncate">{it.filename_original}</span>
                          <span>·</span>
                          <span>{formatBytes(it.file_size_bytes)}</span>
                          {it.date_depot && (
                            <>
                              <span>·</span>
                              <span>
                                Déposée le {new Date(it.date_depot).toLocaleDateString('fr-BE')}
                              </span>
                            </>
                          )}
                          {it.generated_from_id && (
                            <>
                              <span>·</span>
                              <span title="Issue d'un draft IA">
                                <IconInfoCircle
                                  size={11}
                                  stroke={1.6}
                                  className="-mt-0.5 mr-0.5 inline"
                                />
                                issue d'un draft IA
                              </span>
                            </>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <select
                            value={it.origine}
                            onChange={(e) => void onPatchOrigine(it, e.target.value as Origine)}
                            className="rounded border border-rule bg-paper-elevated px-1.5 py-0.5 text-[11px]"
                            title="Origine"
                          >
                            {(Object.keys(ORIGINE_LABELS) as Origine[]).map((o) => (
                              <option key={o} value={o}>
                                {ORIGINE_LABELS[o]}
                              </option>
                            ))}
                          </select>
                          <select
                            value={it.statut}
                            onChange={(e) => void onPatchStatut(it, e.target.value as Statut)}
                            className="rounded border border-rule bg-paper-elevated px-1.5 py-0.5 text-[11px]"
                            title="Statut"
                          >
                            {(Object.keys(STATUT_LABELS) as Statut[]).map((s) => (
                              <option key={s} value={s}>
                                {STATUT_LABELS[s]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {(it.content_type === 'application/pdf' ||
                          it.filename_original.toLowerCase().endsWith('.pdf')) && (
                          <Link
                            to={`/dossiers/${dossierId}/conclusion-files/${it.id}`}
                            title="Ouvrir dans le viewer"
                            className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
                          >
                            <IconEye size={16} stroke={1.6} />
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={() => onDownload(it)}
                          title="Télécharger"
                          className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
                        >
                          <IconDownload size={16} stroke={1.6} />
                        </button>
                        <button
                          type="button"
                          onClick={() => void onDelete(it)}
                          title="Supprimer"
                          className="rounded p-1 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-cat-def-pastel hover:text-cat-def"
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {pending && (
        <UploadModal
          pending={pending}
          setPending={setPending}
          uploading={uploading}
          finalizedDrafts={finalizedDrafts}
          onConfirm={() => void onConfirmUpload()}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

function UploadModal({
  pending,
  setPending,
  uploading,
  finalizedDrafts,
  onConfirm,
  onCancel,
}: {
  pending: PendingUpload;
  setPending: (p: PendingUpload) => void;
  uploading: boolean;
  finalizedDrafts: Conclusion[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-md bg-paper-elevated p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-[16px] font-medium text-text-primary">
          Métadonnées de la conclusion déposée
        </h3>
        <p className="mt-1 text-[12px] text-text-secondary">
          {pending.file.name} · {formatBytes(pending.file.size)}
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
              Titre
            </label>
            <input
              type="text"
              value={pending.title}
              onChange={(e) => setPending({ ...pending, title: e.target.value })}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Origine
              </label>
              <select
                value={pending.origine}
                onChange={(e) => setPending({ ...pending, origine: e.target.value as Origine })}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              >
                {(Object.keys(ORIGINE_LABELS) as Origine[]).map((o) => (
                  <option key={o} value={o}>
                    {ORIGINE_LABELS[o]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Statut
              </label>
              <select
                value={pending.statut}
                onChange={(e) => setPending({ ...pending, statut: e.target.value as Statut })}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              >
                {(Object.keys(STATUT_LABELS) as Statut[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUT_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Date de dépôt
              </label>
              <input
                type="date"
                value={pending.date_depot}
                onChange={(e) => setPending({ ...pending, date_depot: e.target.value })}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Audience visée
              </label>
              <input
                type="text"
                value={pending.audience_visee}
                onChange={(e) => setPending({ ...pending, audience_visee: e.target.value })}
                placeholder="ex. CC 03/06, fond 12/09"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
              Issue d'un draft IA ? (optionnel)
            </label>
            <select
              value={pending.generated_from_id}
              onChange={(e) => setPending({ ...pending, generated_from_id: e.target.value })}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            >
              <option value="">— aucun (rédigée manuellement) —</option>
              {finalizedDrafts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title} ({new Date(d.created_at).toLocaleDateString('fr-BE')})
                </option>
              ))}
            </select>
            {finalizedDrafts.length === 0 && (
              <p className="mt-1 text-[10.5px] text-text-tertiary">
                Aucun draft IA n'a été généré pour ce dossier — passe par l'onglet « Génération
                (drafts IA) » sous Actes pour en créer un.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Juridiction
              </label>
              <input
                type="text"
                value={pending.juridiction}
                onChange={(e) => setPending({ ...pending, juridiction: e.target.value })}
                placeholder="ex. Trib. corr. Liège div. Verviers"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Réf. dépôt (n° rôle)
              </label>
              <input
                type="text"
                value={pending.reference_depot}
                onChange={(e) => setPending({ ...pending, reference_depot: e.target.value })}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
              Commentaire (optionnel)
            </label>
            <textarea
              value={pending.commentaire}
              onChange={(e) => setPending({ ...pending, commentaire: e.target.value })}
              rows={2}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Btn type="button" variant="ghost" onClick={onCancel} disabled={uploading}>
            Annuler
          </Btn>
          <Btn type="button" variant="primary" onClick={onConfirm} disabled={uploading}>
            {uploading ? 'Dépôt…' : 'Confirmer le dépôt'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
